import Cookies from "js-cookie";

/**
 * Permission API response shape: { permissions: [{ screenId, screenCode, screenName, canView, canCreate, canUpdate, canDelete }], fullAccess?: boolean }
 */

const PERMISSIONS_STORAGE_KEY = "userPermissions";

/**
 * Get stored permissions from Cookies or localStorage (set after login from api/v1/user/permissions).
 * Value shape: { permissions: Array, fullAccess: boolean }.
 * @returns {{ permissions: Array, fullAccess: boolean }|null}
 */
export const getStoredPermissions = () => {
  try {
    let raw = Cookies.get(PERMISSIONS_STORAGE_KEY);
    if (!raw && typeof localStorage !== "undefined") {
      raw = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
    }
    if (!raw) return null;
    const data = JSON.parse(raw);
    return {
      permissions: Array.isArray(data?.permissions) ? data.permissions : [],
      fullAccess: Boolean(data?.fullAccess),
    };
  } catch {
    return null;
  }
};

/**
 * Super_admin sees everything; check is done via cookie.
 * @returns {boolean}
 */
const isSuperAdmin = () => {
  try {
    return Cookies.get("userType") === "super_admin";
  } catch {
    return false;
  }
};

/**
 * Get permission object for a screen by screenCode.
 * Super_admin always gets full access. Other users use role-based permissions.
 * @param {string} screenCode - e.g. 'USER_MANAGEMENT', 'SETTINGS'
 * @returns {{ canView, canCreate, canUpdate, canDelete }|null}
 */
export const getPermissionByScreenCode = (screenCode) => {
  if (isSuperAdmin()) {
    return { canView: true, canCreate: true, canUpdate: true, canDelete: true };
  }
  const stored = getStoredPermissions();
  if (!stored) return null;
  if (stored.fullAccess) {
    return { canView: true, canCreate: true, canUpdate: true, canDelete: true };
  }
  const perm = stored.permissions.find(
    (p) => String(p.screenCode || "").toUpperCase() === String(screenCode || "").toUpperCase()
  );
  return perm
    ? {
        canView: Boolean(perm.canView),
        canCreate: Boolean(perm.canCreate),
        canUpdate: Boolean(perm.canUpdate),
        canDelete: Boolean(perm.canDelete),
      }
    : null;
};

/**
 * Check if current user has the given permission for a screen.
 * @param {string} screenCode - e.g. 'USER_MANAGEMENT'
 * @param {'canView'|'canCreate'|'canUpdate'|'canDelete'} action - default 'canView'
 * @returns {boolean}
 */
export const hasScreenAccess = (screenCode, action = "canView") => {
  const perm = getPermissionByScreenCode(screenCode);
  if (!perm) return false;
  return Boolean(perm[action]);
};

/**
 * Whether the user can show the Action column (has any of create/update/delete) for a screen.
 * Super_admin always true.
 * @param {string} screenCode - e.g. 'USER_MANAGEMENT', 'ROLE_MANAGEMENT'
 * @returns {boolean}
 */
export const canShowActionColumn = (screenCode) => {
  const perm = getPermissionByScreenCode(screenCode);
  if (!perm) return false;
  return Boolean(perm.canCreate || perm.canUpdate || perm.canDelete);
};

/**
 * Map of route path (or path prefix) to screenCode for protection and menu visibility.
 * Used by ProtectedRoute and DashboardMenus.
 */
export const ROUTE_TO_SCREEN_CODE = {
  "/dashboard": "ACTIVITY_DASHBOARD",
  "/activty-dashboard": "ACTIVITY_DASHBOARD",
  "/all-users": "USER_MANAGEMENT",
  "/add-user": "USER_MANAGEMENT",
  "/role-management": "ROLE_MANAGEMENT",
  "/settings": "SETTINGS",
  "/asset": "ASSET_MANAGEMENT",
  "/add-asset": "ASSET_MANAGEMENT",
  "/update-assert": "ASSET_MANAGEMENT",
  "/add-vehical": "ASSET_MANAGEMENT",
  "/update-vehicle": "ASSET_MANAGEMENT",
  "/configuration": "CONFIGURATION",
  "/maptask": "TASK_MANAGEMENT",
  "/task-manage": "TASK_MANAGEMENT",
  "/task-manage": "AI_CHAT_AGENT",
  "/listview": "TASK_MANAGEMENT",
  "/export-report-and-data": "ACCOUNT_HISTORY",
  "/workflow": "WORKFLOW",
  "/workflow-form": "WORKFLOW",
  "/reports": "REPORTS",
  "/security": "ACCOUNT_PROTECTION",
  "/createAccount": "ACCOUNT_PROTECTION",
  "/special-form": "ACCOUNT_PROTECTION",
  "/guidelines": "GUIDELINES",
  "/installment-plan": "INSTALMENT_PLAN",
  "/stock-managment": "METER_INVENTORY",
  "/upload-stock-data": "METER_INVENTORY",
  "/organization": "USER_MANAGEMENT",
  "/customer-support": "CUSTOMER_SUPPORT",
  "/call-center/inbox": "CC_INBOX",
  "/call-center/campaigns": "CC_CAMPAIGNS",
  "/call-center/agent-workspace": "CC_AGENT_WORKSPACE",
  "/call-center/tickets": "CC_AGENT_WORKSPACE",
  "/call-center/settings": "CC_SETTINGS",
  "/worklog": "WORKLOG",
  "/pdf-generator": "PDF_REPORT",

};

/** Screen codes for organization list view modes (from login permissions) */
export const DEPARTMENT_SCREEN_CODE = "DEPARTMENT";
export const PROJECT_SCREEN_CODE = "PROJECT";

/**
 * Get screenCode for a path (exact match first, then prefix).
 * @param {string} pathname
 * @returns {string|null}
 */
export const getScreenCodeForPath = (pathname) => {
  const path = pathname?.split("?")[0] || "";
  if (ROUTE_TO_SCREEN_CODE[path]) return ROUTE_TO_SCREEN_CODE[path];
  for (const [route, code] of Object.entries(ROUTE_TO_SCREEN_CODE)) {
    if (path === route || path.startsWith(route + "/")) return code;
  }
  return null;
};

/**
 * Get default route after login based on permissions (first screen with canView).
 * Super_admin and fullAccess go to /dashboard. Falls back to /dashboard if no permissions.
 */
export const getDefaultRouteFromPermissions = () => {
  if (isSuperAdmin()) return "/dashboard";
  const stored = getStoredPermissions();
  if (!stored) return '/dashboard';

  if (stored.fullAccess) return "/dashboard";

  const order = [
    "ACTIVITY_DASHBOARD",
    "USER_MANAGEMENT",
    "ROLE_MANAGEMENT",
    "SETTINGS",
    "ASSET_MANAGEMENT",
    "VEHICLE",
    "CONFIGURATION",
    "ASSIGNMENT_RULES",
    "SPECIAL_ASSIGNMENT_RULES",
    "CUSTOM_VARIABLE",
    "TASK_MANAGEMENT",
    "ACCOUNT_HISTORY",
    "WORKFLOW",
    "REPORTS",
    "ACCOUNT_PROTECTION",
    "CUSTOMER_SUPPORT",
    "GUIDELINES",
    "METER_INVENTORY",
    "AI_CHAT_AGENT",
    "CC_INBOX",
    "CC_CAMPAIGNS",
    "CC_AGENT_WORKSPACE",
    "CC_SETTINGS",
    "WORKLOG",
    "PDF_REPORT",
  ];

  const codeToPath = {
    ACTIVITY_DASHBOARD: "/dashboard",
    USER_MANAGEMENT: "/all-users",
    ROLE_MANAGEMENT: "/role-management",
    SETTINGS: "/settings",
    ASSET_MANAGEMENT: "/asset",
    VEHICLE: "/asset",
    CONFIGURATION: "/configuration",
    ASSIGNMENT_RULES: "/configuration",
    SPECIAL_ASSIGNMENT_RULES: "/configuration",
    CUSTOM_VARIABLE: "/configuration",
    TASK_MANAGEMENT: "/maptask",
    ACCOUNT_HISTORY: "/export-report-and-data",
    WORKFLOW: "/workflow",
    REPORTS: "/reports",
    ACCOUNT_PROTECTION: "/security",
    CUSTOMER_SUPPORT: "/customer-support",
    GUIDELINES: "/guidelines",
    METER_INVENTORY: "/stock-managment",
    AI_CHAT_AGENT: "/task-manage",
    CC_INBOX: "/call-center/inbox",
    CC_CAMPAIGNS: "/call-center/campaigns",
    CC_AGENT_WORKSPACE: "/call-center/agent-workspace",
    CC_SETTINGS: "/call-center/settings",
    WORKLOG: "/worklog",
    PDF_REPORT: "/pdf-generator",
  };

  for (const code of order) {
    const perm = stored.permissions.find(
      (p) => String(p.screenCode || "").toUpperCase() === code
    );
    if (perm?.canView && codeToPath[code]) return codeToPath[code];
  }

  return '/dashboard';
};
